// Shared completion results and provider redirect paths for TWT sign-in.

export type CompleteResponse = {
  status: number;
  body: {
    ok?: boolean;
    code?: string;
    redirectTo?: string;
    email?: string;
    message?: string;
  };
};

export type CompleteOutcome =
  | { kind: "navigate"; href: string }
  | { kind: "error"; message: string; recoverable: boolean };

/** Show the completion result or continue to the server destination. */
export function completeOutcome(response: CompleteResponse): CompleteOutcome {
  const { status, body } = response;
  if (status === 200 && body.ok) {
    return { kind: "navigate", href: body.redirectTo || "/dashboard" };
  }
  if (status === 409 && body.code === "clerk_user_mismatch") {
    return {
      kind: "error",
      recoverable: false,
      message:
        "This browser is now signed in to another account. Request a new code to continue.",
    };
  }
  if (status === 409 && body.code === "identity_conflict") {
    return {
      kind: "error",
      recoverable: false,
      message:
        body.message ||
        "This sign-in could not be matched to one account. Contact support so we can connect it safely.",
    };
  }
  if (status === 403 && body.code === "email_unverified") {
    return {
      kind: "error",
      recoverable: true,
      message: body.message || "Verify your email address to continue.",
    };
  }
  if (status === 503) {
    return {
      kind: "error",
      recoverable: false,
      message: "Sign-in is not available right now. Try again shortly.",
    };
  }
  return {
    kind: "error",
    recoverable: true,
    message: body.message || "Sign-in could not be completed. Try again.",
  };
}

export function clerkEmailSeed(remembered: string): string {
  const value = remembered.trim();
  if (!value || value.includes("*") || !value.includes("@")) return "";
  return value;
}

export type CodeFlowState = {
  /** Monotonic generation. Bumping it invalidates in-flight work. */
  generation: number;
  /** Which flow currently owns the single code-entry step, if any. */
  owner: "clerk" | null;
};

export function createCodeFlowState(): CodeFlowState {
  return { generation: 0, owner: null };
}

/**
 * One code-entry flow at a time: starting a flow takes ownership and bumps
 * the generation so a response from the other flow (or an older attempt of
 * the same flow) is dropped instead of painting over the active step.
 */
export function beginCodeFlow(
  state: CodeFlowState,
  owner: "clerk"
): { generation: number } {
  state.generation += 1;
  state.owner = owner;
  return { generation: state.generation };
}

export function codeFlowIsCurrent(
  state: CodeFlowState,
  handle: { generation: number }
): boolean {
  return state.generation === handle.generation;
}

export function endCodeFlow(state: CodeFlowState) {
  state.generation += 1;
  state.owner = null;
}

/** Return destination carried through the OAuth redirect round trip. */
export function clerkOauthRedirectUrls(input: {
  method: "oauth_google" | "oauth_discord";
  nextPath: string;
}): { redirectUrl: string; redirectUrlComplete: string } {
  const next = encodeURIComponent(input.nextPath || "/dashboard");
  return {
    redirectUrl: `/sso-callback?next=${next}`,
    redirectUrlComplete: `/login/clerk/finish?method=${input.method}&next=${next}`,
  };
}
